import {
  FC,
  ReactNode,
  useState,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
} from 'react';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';

interface WbTabsListContextValue {
  value: string;
  onValueChange: (value: string) => void;
  iconBreakpoint: Breakpoint;
  instanceId: string;
}

const WbTabsInternalContext = createContext<WbTabsListContextValue | undefined>(
  undefined,
);

export const useWbTabsContext = (): WbTabsListContextValue => {
  const context = useContext(WbTabsInternalContext);
  if (!context) {
    throw new Error('useWbTabsContext must be used within WbTabsContext');
  }
  return context;
};

interface WbTabsListContextProps {
  children: ReactNode;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  iconBreakpoint?: Breakpoint;
}

export const WbTabsListContext: FC<WbTabsListContextProps> = ({
  children,
  defaultValue,
  value: controlledValue,
  onValueChange,
  iconBreakpoint = 'sm',
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const id = useId();
  const instanceId = id.replace(/:/g, '');

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, onValueChange],
  );

  const contextValue = useMemo(
    () => ({
      value,
      onValueChange: handleValueChange,
      iconBreakpoint,
      instanceId,
    }),
    [value, handleValueChange, iconBreakpoint, instanceId],
  );

  return (
    <WbTabsInternalContext.Provider value={contextValue}>
      {children}
    </WbTabsInternalContext.Provider>
  );
};
